/**
 * Formats an ISO date string or epoch-ms timestamp (e.g. `Date.now()`) into a
 * human-readable format using the user's locale and timezone, e.g. "Jun 18th, 2026 at 4:18 PM"
 */
export function formatDateTime(iso: string | number): string {
    const date = new Date(iso);
    const locale = navigator.language;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const month = new Intl.DateTimeFormat(locale, { month: "short", timeZone }).format(date);
    const day = date.toLocaleDateString(locale, { day: "numeric", timeZone });
    const year = new Intl.DateTimeFormat(locale, { year: "numeric", timeZone }).format(date);
    const time = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone }).format(date);

    return `${month} ${ordinal(Number(day))}, ${year} at ${time}`;
}

export type TimeOfDay = "morning" | "afternoon" | "evening";

/**
 * Which part of the day `iso` falls in, in the user's local timezone — the same hour
 * formatDateTime renders, bucketed for a "good morning/afternoon/evening" greeting.
 * Morning ends at noon, afternoon at 6pm.
 */
export function getTimeOfDay(iso: string | number): TimeOfDay {
    const date = new Date(iso);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hour = Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone }).format(date));

    if (hour < 12) return "morning";
    if (hour < 18) return "afternoon";
    return "evening";
}

function ordinal(n: number): string {
    const s = [
        "th",
        "st",
        "nd",
        "rd",
    ];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export function getInitialsFrom(fullName: string): string;
export function getInitialsFrom(firstName: string, lastName: string): string;
export function getInitialsFrom(firstName: string, lastName: string, email: string): string;
export function getInitialsFrom(first: string, last?: string, email?: string): string {
    if (last === undefined) {
        const parts = first.split(" ");
        return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
    }
    if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
    if (first) return first.slice(0, 2).toUpperCase();
    if (last) return last.slice(0, 2).toUpperCase();
    if (email) return (email.split("@")[0] ?? "").slice(0, 2).toUpperCase();
    return "";
}

export const formatDuration = (totalSeconds?: number | null): string => {
    if (totalSeconds == null) return "--:--";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const FILE_SIZE_UNITS = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB",
] as const;

/** 1536 → "1.5 KB". One decimal below 10, where it is the difference people notice; whole numbers above. */
export const formatFileSize = (bytes: number): string => {
    let value = Math.max(0, bytes);
    let unit = 0;
    while (value >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
        value /= 1024;
        unit++;
    }
    const rounded = unit === 0 || value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
    return `${rounded} ${FILE_SIZE_UNITS[unit]}`;
};

/**
 * Past its deadline and still outstanding.
 *
 * `completed` is what stops finished work being scolded: a course handed in late is simply done,
 * and marking it overdue reads as an outstanding task. `CourseCard` and `LearnerCourseCard` each
 * spell this rule out themselves as `status !== "COMPLETED"`; this takes a boolean instead so a
 * caller holding a display label rather than the enum can still answer the question.
 *
 * Defaults to `false`, so callers that never had the notion keep what they had.
 */
export const isOverdue = (dueDate: string | null | undefined, completed = false): boolean =>
    !!dueDate && !completed && new Date(dueDate) < new Date();
